/**
 * Public blog API client.
 *
 * Talks to student-agent-api /blog/* endpoints. No auth needed —
 * blog content is public.
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

export interface BlogPostSummary {
  id: string;
  slug: string;
  title: string;
  excerpt: string | null;
  cover_image_url: string | null;
  author_name: string;
  author_avatar_url: string | null;
  tags: string[];
  reading_time_min: number;
  published_at: string | null;
}

export interface BlogPostDetail extends BlogPostSummary {
  content_md: string;
  author_bio: string | null;
  view_count: number;
  status: string;
}

export interface BlogTag {
  tag: string;
  post_count: number;
}

/**
 * Fire-and-forget engagement event. Never blocks or surfaces errors — a failed
 * beacon must never affect reading. Uses sendBeacon when available so the
 * 'read' event survives the user navigating away mid-flight.
 */
export function recordBlogEvent(
  slug: string,
  event: 'open' | 'read' | 'share',
  extra: { platform?: string; dwell_ms?: number } = {}
): void {
  try {
    const body = JSON.stringify({
      slug,
      event,
      referrer: document.referrer || undefined,
      ...extra,
    });
    const url = `${API_BASE_URL}/blog/events`;
    if (navigator.sendBeacon) {
      navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
    } else {
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        keepalive: true,
      }).catch(() => {});
    }
  } catch {
    /* tracking must never break the page */
  }
}

export const blogAPI = {
  async list(opts?: { tag?: string; limit?: number; offset?: number }) {
    const params = new URLSearchParams();
    if (opts?.tag) params.set('tag', opts.tag);
    if (opts?.limit) params.set('limit', String(opts.limit));
    if (opts?.offset) params.set('offset', String(opts.offset));

    const url = `${API_BASE_URL}/blog/posts${params.toString() ? `?${params}` : ''}`;
    const res = await fetch(url);
    if (!res.ok) throw new Error(`Failed to load posts (${res.status})`);
    return (await res.json()) as {
      posts: BlogPostSummary[];
      total: number;
      limit: number;
      offset: number;
    };
  },

  async getBySlug(slug: string): Promise<BlogPostDetail> {
    const res = await fetch(`${API_BASE_URL}/blog/posts/${encodeURIComponent(slug)}`);
    if (res.status === 404) throw new Error('NOT_FOUND');
    if (!res.ok) throw new Error(`Failed to load post (${res.status})`);
    return await res.json();
  },

  async listTags(): Promise<BlogTag[]> {
    const res = await fetch(`${API_BASE_URL}/blog/tags`);
    if (!res.ok) throw new Error(`Failed to load tags (${res.status})`);
    const data = await res.json();
    return data.tags || [];
  },
};
